import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ReportProblemOutlined,
  AccessTimeOutlined,
  AssignmentLateOutlined,
  FactCheckOutlined,
  AssignmentTurnedInOutlined,
  WarningAmberOutlined,
  ReplayOutlined,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { UserContext } from '../../../components/shared/useUser';
import {
  controlAreaExceptionCounts,
  controlsDashboardSummary,
  highRiskSites,
  nextBestActions,
  repeatedFailures,
} from '../../../mocks/data/controlsDashboardData';

interface MetricTileProps {
  label: string;
  value: number;
  subtitle: string;
  accentColor: string;
  icon: React.ReactNode;
}

const MetricTile: React.FC<MetricTileProps> = ({ label, value, subtitle, accentColor, icon }) => {
  const theme = useTheme();
  return (
    <Section style={{ p: 4, mb: 0 }}>
      <Box display="flex"
        alignItems="center"
        justifyContent="space-between">
        <Box flex={1}>
          <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
            {label}
          </Box>
          <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
            {value}
          </Box>
          <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
            {subtitle}
          </Box>
        </Box>
        <Box sx={{ backgroundColor: `${accentColor}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor }}>
          <SvgIcon fontSize="medium">{icon}</SvgIcon>
        </Box>
      </Box>
    </Section>
  );
};

const ControlsDashboardPage: React.FC = () => {
  const { t } = useTranslation('complianceControls');
  const { hasPermissionTo } = useContext(UserContext);

  // lazy: single demo permission gate for this read-only KPI page; a real backend would
  // likely gate each tile/module by its own concept permission per the Demo Screen Structure rule.
  const canView = hasPermissionTo ? hasPermissionTo(['ViewComplianceDashboard']) : true;

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('controlsDashboard.title')}</PageTitle>
        <Pill text={t('noPermissionToView')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle subTitle={t('controlsDashboard.subtitle')}>
            {t('controlsDashboard.title')}
          </PageTitle>
        </Grid>

        <Grid item
          xs={12}>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={3}>
              <MetricTile
                label={t('controlsDashboard.failedControls')}
                value={controlsDashboardSummary.failedControls}
                subtitle={t('controlsDashboard.failedControlsSubtitle')}
                accentColor="#d32f2f"
                icon={<ReportProblemOutlined />}
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <MetricTile
                label={t('controlsDashboard.overdueChecks')}
                value={controlsDashboardSummary.overdueChecks}
                subtitle={t('controlsDashboard.overdueChecksSubtitle')}
                accentColor="#ed6c02"
                icon={<AccessTimeOutlined />}
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <MetricTile
                label={t('controlsDashboard.missedOperationalTasks')}
                value={controlsDashboardSummary.missedOperationalTasks}
                subtitle={t('controlsDashboard.missedOperationalTasksSubtitle')}
                accentColor="#9c27b0"
                icon={<AssignmentLateOutlined />}
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <MetricTile
                label={t('controlsDashboard.openAuditFindings')}
                value={controlsDashboardSummary.openAuditFindings}
                subtitle={t('controlsDashboard.openAuditFindingsSubtitle')}
                accentColor="#1976d2"
                icon={<FactCheckOutlined />}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={4}>
              <MetricTile
                label={t('controlsDashboard.openActionItems')}
                value={controlsDashboardSummary.openActionItems}
                subtitle={t('controlsDashboard.openActionItemsSubtitle')}
                accentColor="#00897b"
                icon={<AssignmentTurnedInOutlined />}
              />
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <MetricTile
                label={t('controlsDashboard.highRiskSites')}
                value={controlsDashboardSummary.highRiskSiteCount}
                subtitle={t('controlsDashboard.highRiskSitesSubtitle')}
                accentColor="#c62828"
                icon={<WarningAmberOutlined />}
              />
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <MetricTile
                label={t('controlsDashboard.repeatedFailures')}
                value={controlsDashboardSummary.repeatedFailureCount}
                subtitle={t('controlsDashboard.repeatedFailuresSubtitle')}
                accentColor="#e65100"
                icon={<ReplayOutlined />}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>{t('controlsDashboard.exceptionsByControlArea')}</TableHeader>
            <Box sx={{ px: 5, pb: 5 }}>
              {controlAreaExceptionCounts.map((row) => (
                <Box key={row.controlArea}
                  sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: 180 }}>
                    <Typography variant="textSM">{row.controlArea}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#1976d220',
                        width: `${Math.min(100, row.openCount * 6)}%`,
                      }}
                    />
                  </Box>
                  <Typography variant="textSM"
                    fontWeight={600}
                    sx={{ width: 30, textAlign: 'right' }}>
                    {row.openCount}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Section>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>{t('controlsDashboard.highRiskSitesTableTitle')}</TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('site')}</TableCell>
                    <TableCell>{t('controlsDashboard.openIssues')}</TableCell>
                    <TableCell>{t('controlsDashboard.overdueActions')}</TableCell>
                    <TableCell>{t('controlsDashboard.lastAuditScore')}</TableCell>
                    <TableCell>{t('controlsDashboard.riskReason')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {highRiskSites.map((site) => (
                    <TableRow key={site.siteId}>
                      <TableCell>{site.siteName}</TableCell>
                      <TableCell>{site.openIssues}</TableCell>
                      <TableCell>{site.overdueActions}</TableCell>
                      <TableCell>{site.lastAuditScore}</TableCell>
                      <TableCell>{site.riskReason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Section>
            <TableHeader>{t('controlsDashboard.repeatedFailuresTableTitle')}</TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('controlArea')}</TableCell>
                    <TableCell>{t('description')}</TableCell>
                    <TableCell>{t('controlsDashboard.occurrences')}</TableCell>
                    <TableCell>{t('controlsDashboard.sitesAffected')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {repeatedFailures.map((failure) => (
                    <TableRow key={failure.id}>
                      <TableCell>{failure.controlArea}</TableCell>
                      <TableCell>{failure.description}</TableCell>
                      <TableCell>{failure.occurrences}</TableCell>
                      <TableCell>{failure.sitesAffected}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Section>
            <TableHeader>{t('controlsDashboard.nextBestActionsTableTitle')}</TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('controlsDashboard.priority')}</TableCell>
                    <TableCell>{t('name')}</TableCell>
                    <TableCell>{t('controlsDashboard.reason')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nextBestActions.map((action) => (
                    <TableRow key={action.id}>
                      <TableCell>{action.priority}</TableCell>
                      <TableCell>{action.title}</TableCell>
                      <TableCell>{action.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>
    </PageArea>
  );
};

export default ControlsDashboardPage;
